import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../Components/ui/button';
import { Input } from '../Components/ui/input';
import { Badge } from '../Components/ui/badge';
import {
  CalendarIcon,
  FileIcon,
  CaretSortIcon,
  ArrowRightIcon,
} from '@radix-ui/react-icons';
import { format } from 'date-fns';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../Components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../Components/ui/table';
import { Calendar } from '../Components/ui/calendar';
import axios from 'axios';
import { ToastContainer } from 'react-toastify';
import { CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { DateRange } from 'react-day-picker';
import { RootState } from '../State/store';
import DropdownMenuDemo from '../Components/Filter/OperatorCampaignDropdown';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../Components/ui/dropdown-menu'; // adjust path if needed
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { MoreHorizontal } from 'lucide-react';
import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import OperatorCampaignListDropdown from '../Components/Filter/OperatorCampaignListDropdown';

interface Campaign {
  campaign_id: number;
  operator_mapping_id: number;
  campaign_name: string;
  channel_type: string;
  campaign_status: string;
  start_date: string;
  end_date: string;
  total_traveller_count: number;
  total_targeted_traveller_count: number;
  total_messages_sent: number;
  final_approval_status: string;
  total_delivered_count: number;
  delivered_whatsapp_count: number;
  delivered_sms_count: number;
  sent_quick_send_count: number;
  delivered_quick_send_count: number;
  sent_test_mode_count: number;
  delivered_test_mode_count: number;
}

const ChannelCarousel: React.FC<{ channels: string[] }> = ({ channels }) => {
  const filteredChannels = channels.filter(Boolean);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSliding, setIsSliding] = useState(true);
  const badgeRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [offsets, setOffsets] = useState<number[]>([]);

  // Measure badge widths for accurate sliding
  useEffect(() => {
    if (filteredChannels.length > 1) {
      setTimeout(() => {
        const allOffsets: number[] = [];
        let sum = 0;
        for (let i = 0; i < filteredChannels.length; i++) {
          allOffsets.push(sum);
          const w = badgeRefs.current[i]?.offsetWidth ?? 0;
          sum += w + 2; // 2px gap
        }
        setOffsets(allOffsets);
      }, 0);
    }
  }, [filteredChannels.length, channels.join(',')]);

  // Animation timing
  useEffect(() => {
    if (filteredChannels.length <= 1) return;
    let timeout: ReturnType<typeof setTimeout>;
    if (isSliding) {
      timeout = setTimeout(() => setIsSliding(false), 4000); // 4s animation
    } else {
      timeout = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % filteredChannels.length);
        setIsSliding(true);
      }, 1000); // 1s pause
    }
    return () => clearTimeout(timeout);
  }, [isSliding, filteredChannels.length]);

  if (filteredChannels.length === 0) {
    return <span className='text-gray-400'>-</span>;
  }
  if (filteredChannels.length === 1) {
    // Single badge with color logic
    const channel = filteredChannels[0];
    return (
      <div className='flex items-center h-7'>
        <Badge
          className={
            channel === 'WhatsApp'
              ? ''
              : channel === 'SMS'
                ? ''
                : channel === 'Push Notification'
                  ? ''
                  : channel === 'Email'
                    ? 'bg-blue-400'
                    : 'bg-gray-400'
          }
          style={
            channel === 'WhatsApp'
              ? { backgroundColor: '#479E98', color: '#fff' }
              : channel === 'SMS'
                ? { backgroundColor: '#DFA548', color: '#fff' }
                : channel === 'Push Notification' || channel === 'Push'
                  ? { backgroundColor: '#B87867', color: '#fff' }
                  : channel === 'RCS' || channel === 'RCS messages'
                    ? { backgroundColor: '#61177E', color: '#fff' }
                    : {}
          }
        >
          {channel}
        </Badge>
      </div>
    );
  }

  // Multiple badges
  return (
    <div className='relative h-7 w-[120px] overflow-hidden'>
      <div
        className='flex items-center'
        style={{
          gap: '2px',
          transform: `translateX(-${offsets[currentIndex] ?? 0}px)`,
          transition: isSliding
            ? 'transform 4s cubic-bezier(0.5,0,0.5,1)'
            : 'none',
        }}
      >
        {filteredChannels.map((channel, idx) => (
          <div
            key={idx}
            ref={(el) => (badgeRefs.current[idx] = el)}
            className='flex items-center'
            style={{ margin: 0, padding: 0 }}
          >
            <Badge
              className={
                channel === 'WhatsApp'
                  ? ''
                  : channel === 'SMS'
                    ? ''
                    : channel === 'Push Notification'
                      ? ''
                      : channel === 'Email'
                        ? 'bg-blue-400'
                        : 'bg-gray-400'
              }
              style={
                channel === 'WhatsApp'
                  ? { backgroundColor: '#479E98', color: '#fff' }
                  : channel === 'SMS'
                    ? { backgroundColor: '#DFA548', color: '#fff' }
                    : channel === 'Push Notification' || channel === 'Push'
                      ? { backgroundColor: '#B87867', color: '#fff' }
                      : channel === 'RCS' || channel === 'RCS messages'
                        ? { backgroundColor: '#61177E', color: '#fff' }
                        : {}
              }
            >
              {channel}
            </Badge>
          </div>
        ))}
      </div>
    </div>
  );
};

const ChannelBadge: React.FC<{ channel: string }> = ({ channel }) => (
  <Badge
    style={
      channel === 'WhatsApp'
        ? { backgroundColor: '#479E98', color: '#fff' }
        : channel === 'SMS'
          ? { backgroundColor: '#DFA548', color: '#fff' }
          : channel === 'Push Notification' || channel === 'Push'
            ? { backgroundColor: '#B87867', color: '#fff' }
            : channel === 'Email'
              ? { backgroundColor: '#2B71B8', color: '#fff' }
              : channel === 'RCS' || channel === 'RCS messages'
                ? { backgroundColor: '#61177E', color: '#fff' }
                : {}
    }
  >
    {channel}
  </Badge>
);

const DatePickerWithRange: React.FC<{
  dateRange: DateRange | undefined;
  setDateRange: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  setInitialCampaign: React.Dispatch<React.SetStateAction<boolean>>;
  setIsClearingDate: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ dateRange, setDateRange, setInitialCampaign, setIsClearingDate }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button
        variant='outline'
        className='min-w-[150px] w-auto justify-start text-left font-normal mt-0'
      >
        <CalendarIcon className='mr-2 h-4 w-4' />
        {dateRange?.from ? (
          dateRange.to ? (
            <>
              {format(dateRange.from, 'LLL dd, y')} -{' '}
              {format(dateRange.to, 'LLL dd, y')}
            </>
          ) : (
            format(dateRange.from, 'LLL dd, y')
          )
        ) : (
          <span>Pick a date</span>
        )}
      </Button>
    </PopoverTrigger>
    <PopoverContent className='w-auto p-0' align='start'>
      <Calendar
        initialFocus
        mode='range'
        defaultMonth={dateRange?.from ?? new Date()}
        selected={dateRange}
        onSelect={(range: DateRange | undefined) => {
          if (
            !range?.from &&
            !range?.to &&
            (dateRange?.from || dateRange?.to)
          ) {
            setIsClearingDate(true);
          }
          setDateRange(range);
          if ((range?.from && range?.to) || (!range?.from && !range?.to)) {
            setInitialCampaign(false);
          }
        }}
        numberOfMonths={1}
      />
    </PopoverContent>
  </Popover>
);

const OperatorCampaignList: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [campaignList, setCampaignList] = useState<Campaign[]>([]);
  const [checkboxSelectedRows, setCheckboxSelectedRows] = useState<number[]>(
    [],
  );
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isClearingSearch, setIsClearingSearch] = useState(false);
  const [isClearingDate, setIsClearingDate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [initialcampaign, setInitialCampaign] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const navigate = useNavigate();

  // --- Filter state & available filter values
  const [filterData, setFilterData] = useState({
    filter: 'None',
    subFilter: '',
  });
  const [startDates, setStartDates] = useState<string[]>([]);

  const [openMenuRowId, setOpenMenuRowId] = useState<number | null>(null);
  const [detailedSummaryModal, setDetailedSummaryModal] = useState<{
    isOpen: boolean;
    campaign: Campaign | null;
  }>({ isOpen: false, campaign: null });

  const OperatorUrl = useSelector(
    (state: RootState) => state.authentication.operatorUrl,
  );
  const operatorId = useSelector(
    (state: RootState) => state.authentication.workspace_id,
  );
  // const operatorId = 1;

  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const totalPages: number =
    totalCount > 0 ? Math.ceil(totalCount / rowsPerPage) : 1;

  // Checkbox logic
  const handleCheckboxRowSelect = (id: number) => {
    setCheckboxSelectedRows((prev) => {
      const newSelectedRows = prev.includes(id)
        ? prev.filter((rowId) => rowId !== id)
        : [...prev, id];
      setIsAllSelected(newSelectedRows.length === campaignList.length);
      return newSelectedRows;
    });
  };
  const handleSelectAll = () => {
    if (isAllSelected) setCheckboxSelectedRows([]);
    else setCheckboxSelectedRows(campaignList.map((c) => c.campaign_id));
    setIsAllSelected(!isAllSelected);
  };

  // const handleStatusNavigate = (campaign_id: number) => {
  //   navigate("/operatorNavbar/operatorcampaignstatus", {
  //     state: { campaignId:campaign_id },
  //   });
  // };

  // Fetch with all filters
  const getCampaignListWithArgs = async ({
    operatorId,
    page,
    rowsPerPage,
    searchTerm,
    startDate,
    endDate,
    criteria,
    subFilter,
  }: {
    operatorId: number;
    page: number;
    rowsPerPage: number;
    searchTerm: string;
    startDate?: string;
    endDate?: string;
    criteria?: string;
    subFilter?: string;
  }) => {
    if (!operatorId || !OperatorUrl) return;
    setIsLoading(true);
    try {
      debugger;
      const response = await axios.get(
        `${OperatorUrl}/GetOperatorCampaignsWithPagination`,
        {
          params: {
            operatorId,
            page,
            rowsPerPage,
            searchTerm: searchTerm?.trim() || null,
            startDate,
            endDate,
            criteria: filterData.filter !== 'None' ? filterData.filter : null,
            subFilter:
              filterData.filter !== 'None' ? filterData.subFilter : null,
          },
        },
      );
      if (response.data.status === 'Success') {
        debugger;
        setCampaignList(response.data.campaignList || []);
        setTotalCount(response.data.campaignCount || 0);
        if (
          response.data.startDates &&
          Array.isArray(response.data.startDates)
        ) {
          setStartDates(response.data.startDates);
        }
      } else {
        setCampaignList([]);
        setTotalCount(0);
        setStartDates([]);
      }
    } catch {
      setCampaignList([]);
      setTotalCount(0);
      setStartDates([]);
    } finally {
      setIsLoading(false);
      setIsClearingSearch(false);
      setIsClearingDate(false);
      if (isFirstLoading) setIsFirstLoading(false);
    }
  };

  // Initial load (no filters)
  useEffect(() => {
    if (!operatorId || !OperatorUrl) return;
    getCampaignListWithArgs({
      operatorId,
      page: currentPage,
      rowsPerPage,
      searchTerm: ' ',
    });
    // eslint-disable-next-line
  }, [operatorId, OperatorUrl]);

  // Debounce search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  // Data reload on page, search, date
  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) return;
    let startDate: string | undefined;
    let endDate: string | undefined;
    const bothDatesSelected = dateRange?.from && dateRange?.to;
    const bothDatesCleared = !dateRange?.from && !dateRange?.to;
    const singleDateSelected = dateRange?.from && !dateRange?.to;
    if (bothDatesSelected) {
      startDate = format(dateRange.from!, 'yyyy-MM-dd');
      endDate = format(dateRange.to!, 'yyyy-MM-dd');
    }
    if (
      (bothDatesSelected || bothDatesCleared || initialcampaign) &&
      !singleDateSelected
    ) {
      getCampaignListWithArgs({
        operatorId,
        page: currentPage,
        rowsPerPage,
        searchTerm: debouncedSearchTerm,
        startDate,
        endDate,
      });
    }
    // eslint-disable-next-line
  }, [
    currentPage,
    rowsPerPage,
    debouncedSearchTerm,
    dateRange?.from,
    dateRange?.to,
    initialcampaign,
  ]);

  // --- FILTER & SUBFILTER EFFECT (CORRECT LOGIC) ---
  useEffect(() => {
    // - API is called ONLY when BOTH filter and subfilter are set, or when resetting to "None"
    // - No API call if only filter (criteria) is selected
    if (!operatorId || !OperatorUrl) return;

    if (filterData.filter === 'None') {
      // Reset: show all data
      getCampaignListWithArgs({
        operatorId,
        page: 1,
        rowsPerPage,
        searchTerm: searchTerm.trim() || ' ',
        startDate: dateRange?.from
          ? format(dateRange.from, 'yyyy-MM-dd')
          : undefined,
        endDate: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
      });
      setCurrentPage(1);
      return;
    }

    if (
      filterData.filter !== 'None' &&
      filterData.subFilter &&
      filterData.subFilter.trim() !== ''
    ) {
      // Both filter and subfilter chosen
      getCampaignListWithArgs({
        operatorId,
        page: 1,
        rowsPerPage,
        searchTerm: searchTerm.trim() || ' ',
        startDate: dateRange?.from
          ? format(dateRange.from, 'yyyy-MM-dd')
          : undefined,
        endDate: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined,
        criteria: filterData.filter,
        subFilter: filterData.subFilter,
      });
      setCurrentPage(1);
    }
    // If only filter is set, do nothing (no API call)
  }, [
    filterData.filter,
    filterData.subFilter,
    operatorId,
    OperatorUrl,
    rowsPerPage,
    searchTerm,
    dateRange?.from,
    dateRange?.to,
  ]);

  // Sorting
  const sortCampaignList = (tableHeader: string) => {
    let field: keyof Campaign;
    let type: 'string' | 'number' | 'date' = 'string';
    switch (tableHeader) {
      case 'ByCampaignName':
        field = 'campaign_name';
        type = 'string';
        break;
      case 'ByCampaignChannel':
        field = 'channel_type';
        type = 'string';
        break;
      case 'ByCampaignStatus':
        field = 'campaign_status';
        type = 'string';
        break;
      case 'ByCampaignDate':
        field = 'start_date';
        type = 'date';
        break;
      case 'ByTotalTravellers':
        field = 'total_traveller_count';
        type = 'number';
        break;
      case 'ByCampaignSent':
        field = 'total_targeted_traveller_count';
        type = 'number';
        break;
      case 'ByMessagesSent':
        field = 'total_messages_sent';
        type = 'number';
        break;
      case 'ByApprovalStatus':
        field = 'final_approval_status';
        type = 'string';
        break;
      default:
        return;
    }
    let direction: 'asc' | 'desc' = 'asc';
    if (sortField === field) {
      direction = sortDirection === 'asc' ? 'desc' : 'asc';
    }
    setSortField(field);
    setSortDirection(direction);
    const sortedCampaigns = [...campaignList].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];
      if (type === 'number') {
        aValue = Number(aValue);
        bValue = Number(bValue);
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      } else if (type === 'date') {
        aValue = Date.parse(aValue as string);
        bValue = Date.parse(bValue as string);
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        return direction === 'asc'
          ? String(aValue || '').localeCompare(String(bValue || ''))
          : String(bValue || '').localeCompare(String(aValue || ''));
      }
    });
    setCampaignList(sortedCampaigns);
  };

  // Export — fetches the FULL (unpaginated) list honoring current filters,
  // rather than exporting just the currently visible page.
  const handleExportButtonClick = async () => {
    if (!operatorId || !OperatorUrl) return;

    setIsExporting(true);
    try {
      let startDate: string | undefined;
      let endDate: string | undefined;
      if (dateRange?.from && dateRange?.to) {
        startDate = format(dateRange.from, 'yyyy-MM-dd');
        endDate = format(dateRange.to, 'yyyy-MM-dd');
      }

      const response = await axios.get(
        `${OperatorUrl}/GetOperatorCampaignsWithOutPagination`,
        {
          params: {
            operatorId,
            searchTerm: searchTerm.trim() || null,
            startDate,
            endDate,
            criteria: filterData.filter !== 'None' ? filterData.filter : null,
            subFilter:
              filterData.filter !== 'None' ? filterData.subFilter : null,
          },
        },
      );

      if (response.data.status !== 'Success') {
        return;
      }

      const fullCampaignList: Campaign[] = response.data.campaignList || [];

      debugger;

      const filteredData = fullCampaignList.map(
        ({
          campaign_id,
          campaign_name,
          channel_type,
          campaign_status,
          final_approval_status,
          start_date,
          end_date,
          total_traveller_count,
          total_targeted_traveller_count,
          total_messages_sent,
          total_delivered_count,
          delivered_whatsapp_count,
          delivered_sms_count,
          sent_quick_send_count: quick_send_sent_messages,
          delivered_quick_send_count: quick_send_delivered_messages,
          sent_test_mode_count: test_mode_sent_messages,
          delivered_test_mode_count: test_mode_delivered_messages,
        }) => ({
          campaign_id,
          campaign_name,
          channel_type,
          campaign_status,
          final_approval_status,
          start_date,
          end_date,
          total_traveller_count,
          total_targeted_traveller_count,
          total_messages_sent,
          total_delivered_count,
          delivered_whatsapp_count,
          delivered_sms_count,
          quick_send_sent_messages,
          quick_send_delivered_messages,
          test_mode_sent_messages,
          test_mode_delivered_messages,
        }),
      );

      const worksheet = XLSX.utils.json_to_sheet(filteredData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        'Operator_Campaign_List',
      );
      XLSX.writeFile(workbook, 'OperatorCampaignList.xlsx');
    } catch (err) {
      console.error('Error exporting campaign list:', err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <ToastContainer />
      {isFirstLoading ? (
        <div className='flex flex-col items-center justify-center h-[500px]'>
          <CircularProgress color='primary' />
        </div>
      ) : (
        <div className='mt-2'>
          {/* Filter Row */}
          <div className='flex mt-2'>
            <div>
              <Input
                placeholder='Search campaign by name...'
                className='w-[350px] text-[14px] font-normal text-[#171717]'
                value={searchTerm}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (searchTerm.trim() !== '' && newValue.trim() === '') {
                    setIsClearingSearch(true);
                  }
                  setSearchTerm(newValue);
                  if (currentPage !== 1) setCurrentPage(1);
                }}
              />
            </div>
            <div className='flex items-end ml-auto'>
              <div className='mb-6 mr-4'>
                <OperatorCampaignListDropdown
                  filterData={filterData}
                  setFilterData={setFilterData}
                  dateList={startDates} // from API response
                />
              </div>
              <div className='mb-6'>
                <DatePickerWithRange
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  setInitialCampaign={setInitialCampaign}
                  setIsClearingDate={setIsClearingDate}
                />
              </div>
              {/* <DropdownMenuDemo
                setFilterData={setFilterData}
                filterData={filterData}
                dateList={startDates}
              /> */}

              <Button
                variant='outline'
                className='w-24 mb-6 ml-4 mt-[-6] text-[#020617]'
                onClick={handleExportButtonClick}
                disabled={isExporting}
              >
                {isExporting ? (
                  'Exporting...'
                ) : (
                  <>
                    <FileIcon
                      style={{ width: '14px', height: '14px' }}
                      className='mr-1 font-bold stroke-[2]'
                    />{' '}
                    Export
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className='rounded-md border mt-2'>
            <div className='max-h-[60vh] overflow-y-auto'>
              <Table
                className='rounded-xl whitespace-nowrap border-gray-200'
                style={{ color: '#020202', fontSize: '15px' }}
              >
                <TableHeader>
                  <TableRow>
                    <TableHead className='pl-6 pr-3 py-3 font-medium'>
                      <div className='flex items-center gap-3'>
                        <input
                          type='checkbox'
                          className={`accent-gray-700`}
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                        />
                        <span>Campaign</span>
                        <CaretSortIcon
                          onClick={() => sortCampaignList('ByCampaignName')}
                          className='cursor-pointer'
                        />
                      </div>
                    </TableHead>
                    <TableHead className='py-3 px-3 font-medium w-[140px]'>
                      <div className='flex items-center gap-2'>
                        Channel
                        <CaretSortIcon
                          onClick={() => sortCampaignList('ByCampaignChannel')}
                          className='cursor-pointer'
                        />
                      </div>
                    </TableHead>
                    <TableHead className='py-3 px-3 font-medium'>
                      <div className='flex items-center gap-2'>
                        Campaign Status
                        <CaretSortIcon
                          onClick={() => sortCampaignList('ByCampaignStatus')}
                          className='cursor-pointer'
                        />
                      </div>
                    </TableHead>
                    <TableHead className='py-3 px-3 font-medium'>
                      <div className='flex items-center gap-2'>
                        Approval Status
                        <CaretSortIcon
                          onClick={() => sortCampaignList('ByApprovalStatus')}
                          className='cursor-pointer'
                        />
                      </div>
                    </TableHead>
                    <TableHead className='py-3 px-3 font-medium'>
                      <div className='flex items-center gap-2'>
                        Schedule
                        <CaretSortIcon
                          onClick={() => sortCampaignList('ByCampaignDate')}
                          className='cursor-pointer'
                        />
                      </div>
                    </TableHead>
                    <TableHead className='py-3 px-3 font-medium'>
                      <div className='flex items-center gap-2'>
                        Total Travellers
                        <CaretSortIcon
                          onClick={() => sortCampaignList('ByTotalTravellers')}
                          className='cursor-pointer'
                        />
                      </div>
                    </TableHead>
                    <TableHead className='py-3 px-3 font-medium'>
                      {/* <div className="flex items-center gap-2">
                        People Reached
                        <CaretSortIcon
                          onClick={() => sortCampaignList("ByCampaignSent")}
                          className="cursor-pointer"
                        />
                      </div> */}
                      <div className='flex items-center gap-2'>
                        Targeted Travellers
                        <CaretSortIcon
                          onClick={() => sortCampaignList('ByCampaignSent')}
                          className='cursor-pointer'
                        />
                      </div>
                    </TableHead>
                    <TableHead className='py-3 px-3 font-medium'>
                      <div className='flex items-center gap-2'>
                        Messages Sent
                        <CaretSortIcon
                          onClick={() => sortCampaignList('ByCampaignSent')}
                          className='cursor-pointer'
                        />
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaignList.length > 0 && !isLoading ? (
                    campaignList.map((campaign) => {
                      const isSelected = checkboxSelectedRows.includes(
                        campaign.campaign_id,
                      );
                      return (
                        <TableRow
                          key={campaign.campaign_id}
                          className={isSelected ? 'bg-gray-200' : ''}
                        >
                          <TableCell className='pl-6 pr-3 py-4'>
                            <div className='flex items-center gap-3'>
                              <input
                                type='checkbox'
                                className='accent-gray-700'
                                checked={isSelected}
                                onChange={() =>
                                  handleCheckboxRowSelect(campaign.campaign_id)
                                }
                              />
                              <span
                                className='text-black text-[14px] cursor-pointer font-normal hover:text-blue-700 transition'
                                onClick={() =>
                                  navigate(
                                    '/operatorNavbar/operatorcampaignapproval',
                                    {
                                      state: {
                                        campaignId: campaign.campaign_id,
                                        operatorMappingId:
                                          campaign.operator_mapping_id,
                                      },
                                    },
                                  )
                                }
                              >
                                {campaign.campaign_name}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className='py-4 px-3 align-middle text-left'>
                            <ChannelCarousel
                              channels={
                                typeof campaign.channel_type === 'string'
                                  ? campaign.channel_type
                                      .split(',')
                                      .map((ch) => ch.trim())
                                  : []
                              }
                            />
                          </TableCell>

                          <TableCell className='py-4 px-3 align-middle text-left'>
                            {campaign.campaign_status}
                          </TableCell>
                          <TableCell className='py-4 px-3 text-left align-middle'>
                            {campaign.final_approval_status}
                          </TableCell>
                          <TableCell className='py-4 px-3 align-middle'>
                            <div className='flex items-center gap-2'>
                              {campaign.start_date ? (
                                <>
                                  {new Date(campaign.start_date)
                                    .toLocaleDateString('en-GB', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      year: 'numeric',
                                    })
                                    .replace(/\//g, '/')}
                                  <ArrowRightIcon />
                                  {campaign.end_date
                                    ? new Date(campaign.end_date)
                                        .toLocaleDateString('en-GB', {
                                          day: '2-digit',
                                          month: '2-digit',
                                          year: 'numeric',
                                        })
                                        .replace(/\//g, '/')
                                    : '-'}
                                </>
                              ) : (
                                '-'
                              )}
                            </div>
                          </TableCell>
                          <TableCell className='py-4 px-3 text-center align-middle'>
                            {typeof campaign.total_traveller_count !==
                            'undefined'
                              ? campaign.total_traveller_count
                              : '-'}
                          </TableCell>
                          <TableCell className='py-4 px-3 text-center align-middle'>
                            {typeof campaign.total_targeted_traveller_count !==
                            'undefined'
                              ? campaign.total_targeted_traveller_count
                              : '-'}
                          </TableCell>
                          <TableCell className='py-4 px-3 text-center align-middle'>
                            {typeof campaign.total_messages_sent !== 'undefined'
                              ? campaign.total_messages_sent
                              : '-'}
                          </TableCell>
                          <TableCell className='py-2 px-2 text-left align-middle'>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant='ghost'
                                  size='icon'
                                  className='mt-0'
                                >
                                  <DotsHorizontalIcon className='cursor-pointer w-5 h-5' />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align='end'>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setOpenMenuRowId(null); // Close the menu
                                    setDetailedSummaryModal({
                                      isOpen: true,
                                      campaign: campaign,
                                    });
                                  }}
                                  className='px-3 py-2 hover:bg-gray-100 cursor-pointer'
                                >
                                  View Detailed Summary
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={9} // ✅ Change from 7 to 9 (you have 9 columns)
                        className='text-center py-4 text-gray-500 font-medium'
                      >
                        {isLoading || isClearingSearch || isClearingDate
                          ? 'Loading...'
                          : searchTerm.trim() !== ''
                            ? 'No campaigns found for the searched item.'
                            : dateRange?.from && dateRange?.to
                              ? 'No campaigns found within selected date range.'
                              : filterData.filter !== 'None' &&
                                  filterData.subFilter !== ''
                                ? 'No campaigns found for the filtered item.' // ✅ Better message
                                : 'No campaigns found.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Detailed Summary Dialog */}
          <Dialog
            open={detailedSummaryModal.isOpen}
            onClose={() =>
              setDetailedSummaryModal({ isOpen: false, campaign: null })
            }
            aria-labelledby='detailed-summary-title'
            maxWidth='xs'
            fullWidth={false}
            PaperProps={{
              sx: {
                borderRadius: '10px',
                p: 1,
                width: 420,
                maxWidth: 'calc(100% - 32px)',
              },
            }}
          >
            <DialogTitle id='detailed-summary-title' sx={{ pb: '12px' }}>
              <div className='text-left text-base font-semibold text-gray-900'>
                Campaign Name :{' '}
                <span className='text-[#3A85F7]'>
                  {detailedSummaryModal.campaign?.campaign_name}
                </span>
              </div>
            </DialogTitle>

            <DialogContent sx={{ pt: 0 }}>
              <div className='space-y-5'>
                {/* Campaign Section */}
                <div>
                  <h3 className='text-sm font-semibold text-gray-800 mb-2'>
                    Campaign
                  </h3>

                  <div className='space-y-2'>
                    {detailedSummaryModal.campaign?.channel_type?.includes(
                      'WhatsApp',
                    ) && (
                      <div
                        className='flex justify-between items-center p-3 rounded-lg'
                        style={{
                          backgroundColor: 'rgba(71, 158, 152, 0.10)',
                          border: '1px solid #479E98',
                        }}
                      >
                        <div className='flex items-center gap-2'>
                          <div
                            className='w-3 h-3 rounded-full'
                            style={{ backgroundColor: '#479E98' }}
                          />
                          <span className='text-sm font-medium'>
                            WhatsApp Delivered
                          </span>
                        </div>

                        <span
                          className='font-semibold'
                          style={{ color: '#479E98' }}
                        >
                          {detailedSummaryModal.campaign
                            ?.delivered_whatsapp_count || 0}
                        </span>
                      </div>
                    )}

                    {detailedSummaryModal.campaign?.channel_type?.includes(
                      'SMS',
                    ) && (
                      <div
                        className='flex justify-between items-center p-3 rounded-lg'
                        style={{
                          backgroundColor: 'rgba(223, 165, 72, 0.12)',
                          border: '1px solid #DFA548',
                        }}
                      >
                        <div className='flex items-center gap-2'>
                          <div
                            className='w-3 h-3 rounded-full'
                            style={{ backgroundColor: '#DFA548' }}
                          />
                          <span className='text-sm font-medium'>
                            SMS Delivered
                          </span>
                        </div>

                        <span
                          className='font-semibold'
                          style={{ color: '#DFA548' }}
                        >
                          {detailedSummaryModal.campaign?.delivered_sms_count ||
                            0}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick Send Section */}
                <div>
                  <h3 className='text-sm font-semibold text-gray-800 mb-2'>
                    Quick Send
                  </h3>

                  <div className='space-y-2'>
                    {/* Quick Send Sent */}
                    <div
                      className='flex justify-between items-center p-3 rounded-lg'
                      style={{
                        backgroundColor: 'rgba(59, 130, 246, 0.10)',
                        border: '1px solid #3B82F6',
                      }}
                    >
                      <div className='flex items-center gap-2'>
                        <div
                          className='w-3 h-3 rounded-full'
                          style={{ backgroundColor: '#3B82F6' }}
                        />
                        <span className='text-sm font-medium text-gray-700'>
                          Sent Messages
                        </span>
                      </div>

                      <span
                        className='font-semibold'
                        style={{ color: '#3B82F6' }}
                      >
                        {detailedSummaryModal.campaign?.sent_quick_send_count}
                      </span>
                    </div>

                    {/* Quick Send Delivered */}
                    <div
                      className='flex justify-between items-center p-3 rounded-lg'
                      style={{
                        backgroundColor: 'rgba(139, 92, 246, 0.10)',
                        border: '1px solid #8B5CF6',
                      }}
                    >
                      <div className='flex items-center gap-2'>
                        <div
                          className='w-3 h-3 rounded-full'
                          style={{ backgroundColor: '#8B5CF6' }}
                        />
                        <span className='text-sm font-medium text-gray-700'>
                          Delivered Messages
                        </span>
                      </div>

                      <span
                        className='font-semibold'
                        style={{ color: '#8B5CF6' }}
                      >
                        {
                          detailedSummaryModal.campaign
                            ?.delivered_quick_send_count
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {/* Test Mode Section */}
                <div>
                  <h3 className='text-sm font-semibold text-gray-800 mb-2'>
                    Test Mode
                  </h3>

                  <div className='space-y-2'>
                    {/* Test Mode Sent */}
                    <div
                      className='flex justify-between items-center p-3 rounded-lg'
                      style={{
                        backgroundColor: 'rgba(249, 115, 22, 0.10)',
                        border: '1px solid #F97316',
                      }}
                    >
                      <div className='flex items-center gap-2'>
                        <div
                          className='w-3 h-3 rounded-full'
                          style={{ backgroundColor: '#F97316' }}
                        />
                        <span className='text-sm font-medium text-gray-700'>
                          Sent Messages
                        </span>
                      </div>

                      <span
                        className='font-semibold'
                        style={{ color: '#F97316' }}
                      >
                        {detailedSummaryModal.campaign?.sent_test_mode_count}
                      </span>
                    </div>

                    {/* Test Mode Delivered */}
                    <div
                      className='flex justify-between items-center p-3 rounded-lg'
                      style={{
                        backgroundColor: 'rgba(236, 72, 153, 0.10)',
                        border: '1px solid #EC4899',
                      }}
                    >
                      <div className='flex items-center gap-2'>
                        <div
                          className='w-3 h-3 rounded-full'
                          style={{ backgroundColor: '#EC4899' }}
                        />
                        <span className='text-sm font-medium text-gray-700'>
                          Delivered Messages
                        </span>
                      </div>

                      <span
                        className='font-semibold'
                        style={{ color: '#EC4899' }}
                      >
                        {
                          detailedSummaryModal.campaign
                            ?.delivered_test_mode_count
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </DialogContent>

            <DialogActions sx={{ pt: '8px', justifyContent: 'center' }}>
              <Button
                variant='outline'
                onClick={() =>
                  setDetailedSummaryModal({ isOpen: false, campaign: null })
                }
                className='w-20 cursor-pointer'
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>
          {/* Pagination */}
          <div className='flex justify-between items-center mt-4'>
            <div className='flex items-center space-x-2 text-gray-500 text-sm '>
              <span>
                {totalCount === 0
                  ? '0 of 0 row(s) selected'
                  : `${(currentPage - 1) * rowsPerPage + 1}-${Math.min(currentPage * rowsPerPage, totalCount)} of ${totalCount} row(s) selected`}
              </span>
            </div>
            <div className='flex items-center space-x-4 font-medium text-sm'>
              <span className='text-[#020617] font-medium text-[14px]'>
                Rows per page
              </span>
              <div className='relative inline-block ml-2'>
                <select
                  disabled={campaignList.length === 0}
                  className={`cursor-pointer border border-gray-300 rounded-md px-2 py-1 appearance-none focus:outline-none focus:ring-1 focus:ring-blue-500 ${campaignList.length === 0 ? 'cursor-not-allowed bg-gray-100' : ''}`}
                  style={{
                    width: '60px',
                    height: '30px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '400',
                    borderColor: '#E5E7EB',
                    boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)',
                  }}
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {[5, 10, 20].map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
                <CaretSortIcon className='absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500 w-4 h-4' />
              </div>
              <div className='ml-4 mr-4'>
                <span className='text-[#020617] text-[14px] font-medium'>{`Page ${currentPage} of ${totalPages}`}</span>
              </div>
              <div className='flex items-center gap-2'>
                <button
                  disabled={currentPage === 1 || totalCount === 0}
                  className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${
                    currentPage === 1
                      ? 'cursor-not-allowed bg-gray-100'
                      : 'hover:bg-gray-200'
                  }`}
                  onClick={() => setCurrentPage(1)}
                >
                  «
                </button>
                <button
                  disabled={currentPage === 1 || totalCount === 0}
                  className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${
                    currentPage === 1
                      ? 'cursor-not-allowed bg-gray-100'
                      : 'hover:bg-gray-200'
                  }`}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  ‹
                </button>
                <button
                  disabled={currentPage === totalPages || totalCount === 0}
                  className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${
                    currentPage === totalPages
                      ? 'cursor-not-allowed bg-gray-100'
                      : 'hover:bg-gray-200'
                  }`}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  ›
                </button>
                <button
                  disabled={currentPage === totalPages || totalCount === 0}
                  className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${
                    currentPage === totalPages
                      ? 'cursor-not-allowed bg-gray-100'
                      : 'hover:bg-gray-200'
                  }`}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  »
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OperatorCampaignList;
